/**
 * `TransferAction` — the registered `React.FC<SlotProps>` container for the Transfer
 * action: renders the icon-only trigger, and owns everything `TransferModal` (presentational) does
 * not — reading `api` from `useCodingScreen()`, deriving the "Logged As" identity and the recipient
 * / dictionary data from the bound slot `value`, the open/close + submission state machine, and the
 * trigger ref the modal restores focus to. Mirrors `HoldAction` (CDMX-18412).
 *
 * ONE BOUND VALUE. A slot has exactly one `contentId`, so the modal's two data needs (who is acting,
 * and what may be picked) arrive together as `TransferActionData` at `contentId: 'case.transfer'` —
 * not as two bindings, and never by reaching into another slot's data. Binding under `case.*` also
 * makes the slot coding-only mechanically: `validateConfig` rejects `case.*` in a grading config.
 *
 * PERMISSION. `data.permission` must be `'allowed'` for the trigger to render -- anything else
 * (`'denied'`, `'loading'`, or absent) hides it, fail-closed.
 */
import { Box, Button, Tooltip, useToast } from '@chakra-ui/react';
import React, { useRef, useState } from 'react';

import { useCodingScreen } from '../../interface/context';
import type { SlotProps } from '../../interface/slotProps';
import {
  type QueueListState,
  type TransferActionData,
  TransferMoveUnconfirmedError,
  TransferNotRecordedError,
  TransferNotSentError,
  TransferOutcomeUnknownError,
  type TransferPayload
} from '../../interface/transfer';
import { CASE_ACTION_RADIUS, TRANSFER_COLORS } from '../../styles/tokens';
import { TransferModal, type TransferSubmitState } from '../TransferModal';

const TRANSFER_LABEL = 'Transfer';

/** Narrow the slot `value` (unknown) to TransferActionData: only a genuine non-null non-array
 *  object; everything else (undefined/null/array/primitive) -> {} so the modal falls back to its
 *  own defaults and this component never throws. Same guard shape as `HoldAction`. */
const asTransferData = (value: unknown): TransferActionData =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as TransferActionData)
    : {};

const nameOf = (data: TransferActionData): string => {
  const displayName = data.currentUser?.displayName;
  return typeof displayName === 'string' && displayName.trim().length > 0
    ? displayName
    : 'XXX'; // placeholder until a real current user is injected (same as HoldAction)
};

export const TransferAction: React.FC<SlotProps> = ({ value }) => {
  const { api } = useCodingScreen();
  const data = asTransferData(value);
  const [isOpen, setIsOpen] = useState(false);
  const [loggedAs, setLoggedAs] = useState<{ name: string; at: Date } | null>(
    null
  );
  const [submitState, setSubmitState] = useState<TransferSubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Bumped per open and used as the modal's `key` -- remounts it fresh each time, so no stale
  // recipient/reason/validation state can survive a close->open.
  const [openGen, setOpenGen] = useState(0);
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [queues, setQueues] = useState<QueueListState>({ status: 'loading' });
  const inFlightRef = useRef(false);
  const queueLoadSeqRef = useRef(0);

  if (data.permission !== 'allowed') return null;

  const loadQueues = (): void => {
    const seq = ++queueLoadSeqRef.current;
    if (!api?.listTransferQueues) {
      setQueues({ status: 'ready', queues: [] });
      return;
    }
    setQueues({ status: 'loading' });
    api.listTransferQueues().then(
      (loaded) => {
        if (seq !== queueLoadSeqRef.current) return;
        setQueues({ status: 'ready', queues: loaded });
      },
      (error: unknown) => {
        if (seq !== queueLoadSeqRef.current) return;
        setQueues({
          status: 'error',
          message:
            error instanceof Error && error.message
              ? error.message
              : "Couldn't load queues."
        });
      }
    );
  };

  const open = (): void => {
    setLoggedAs({ name: nameOf(data), at: new Date() }); // frozen snapshot per open
    loadQueues();
    setSubmitState('idle');
    setErrorMessage(undefined);
    setStatus('');
    setOpenGen((g) => g + 1);
    setIsOpen(true);
  };
  const close = (): void => {
    setIsOpen(false);
    setSubmitState('idle');
    setErrorMessage(undefined);
  };

  const onSubmit = async (payload: TransferPayload): Promise<void> => {
    setErrorMessage(undefined);
    if (!api?.transferCase) {
      setSubmitState('error');
      setErrorMessage(
        "Transfer isn't available yet — nothing was saved and the case was not moved."
      );
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setSubmitState('transferring');
    try {
      const { destinationName } = await api.transferCase(payload);
      const text = `Case transferred to ${destinationName}.`;
      setStatus(text);
      toast({
        title: text,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      close();
      api.navigateToWorklist?.();
    } catch (e) {
      if (e instanceof TransferNotSentError) {
        setSubmitState('error');
        setErrorMessage(e.message);
        return;
      }
      const text =
        e instanceof TransferMoveUnconfirmedError ||
        e instanceof TransferNotRecordedError ||
        e instanceof TransferOutcomeUnknownError
          ? e.message
          : 'The transfer may not have completed. Check the case owner in the worklist.';
      setStatus(text);
      toast({
        title: text,
        status: 'warning',
        duration: null,
        isClosable: true
      });
      close();
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <>
      <Tooltip label={TRANSFER_LABEL} placement="bottom" openDelay={300}>
        <Button
          ref={triggerRef}
          data-testid="transfer-icon"
          aria-label={TRANSFER_LABEL}
          onClick={open}
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          minW="auto"
          h="auto"
          p={0}
        >
          <Box
            aria-hidden
            bg={TRANSFER_COLORS.iconBg}
            borderRadius={CASE_ACTION_RADIUS.control}
            w="25px"
            h="25px"
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* North-east arrow, drawn inline: no allow-listed icon atom carries one, and the
                boundary guard forbids reaching for another package. Same call as HoldAction's
                hand-drawn pause glyph. */}
            <Box
              as="svg"
              viewBox="0 0 24 24"
              width="15px"
              height="15px"
              fill="none"
              stroke={TRANSFER_COLORS.iconArrow}
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 17 17 7M9 7h8v8" />
            </Box>
          </Box>
        </Button>
      </Tooltip>
      <Box
        data-testid="transfer-status"
        role="status"
        aria-live="polite"
        position="absolute"
        width="1px"
        height="1px"
        overflow="hidden"
        clipPath="inset(50%)"
        whiteSpace="nowrap"
      >
        {status}
      </Box>
      {loggedAs && (
        <TransferModal
          key={openGen}
          isOpen={isOpen}
          onClose={close}
          loggedAs={loggedAs}
          data={data}
          onSearch={api?.searchTransferRecipients}
          queues={queues}
          onRetryQueues={loadQueues}
          onSubmit={onSubmit}
          submitState={submitState}
          errorMessage={errorMessage}
          canSubmit={Boolean(api?.transferCase)}
          finalFocusRef={triggerRef}
        />
      )}
    </>
  );
};
